import React from 'react';
import { Stack, FormControl, FormLabel, Input, Textarea, Typography, Box, Select, Option } from '@mui/joy';

interface CreatePositionStepProps {
  data: {
    jobTitle: string;
    visibility: 'public' | 'unlisted' | 'inactive';
    isPartTime: boolean;
    isFullTime: boolean;
    description: string;
  };
  onUpdate: (updates: any) => void;
}

const CreatePositionStep: React.FC<CreatePositionStepProps> = ({ data, onUpdate }) => {
  return (
    <Stack spacing={4}>
      {/* Job Title */}
      <FormControl>
        <FormLabel sx={{ fontWeight: 600, color: '#1A1B1D', mb: 1 }}>
          Job Title *
        </FormLabel>
        <Input
          value={data.jobTitle}
          onChange={(e) => onUpdate({ jobTitle: e.target.value })}
          placeholder="e.g. Server, Manager, All Front-of-House"
          sx={{
            height: '56px',
            backgroundColor: '#ECEFF3',
            borderRadius: '12px',
            border: 'none',
            fontSize: '1.125rem',
            color: '#1A1B1D',
            '--Input-focusedHighlight': 'transparent !important',
            '&::placeholder': {
              color: '#7B7F85 !important',
              opacity: 1,
            },
            '&:hover': {
              backgroundColor: '#ECEFF3',
            },
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              backgroundColor: '#ECEFF3',
            },
          }}
        />
      </FormControl>

      {/* Visibility */}
      <FormControl>
        <FormLabel sx={{ fontWeight: 600, color: '#1A1B1D', mb: 1 }}>
          Visibility
        </FormLabel>
        <Select
          value={data.visibility}
          onChange={(_, value) => onUpdate({ visibility: value as 'public' | 'unlisted' | 'inactive' })}
          sx={{
            height: '56px',
            backgroundColor: '#ECEFF3',
            borderRadius: '12px',
            border: 'none',
            fontSize: '1.125rem',
            color: '#1A1B1D',
            '--Input-focusedHighlight': 'transparent !important',
            '&::placeholder': {
              color: '#7B7F85 !important',
              opacity: 1,
            },
            '&:hover': {
              backgroundColor: '#ECEFF3',
            },
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              backgroundColor: '#ECEFF3',
            },
          }}
        >
          <Option value="public">Public - everyone can find and apply to your post</Option>
          <Option value="unlisted">Unlisted - only people with the link can find and apply to your post</Option>
          <Option value="inactive">Inactive - only you can see this post</Option>
        </Select>
      </FormControl>

      {/* Employment Type */}
      <FormControl>
        <FormLabel sx={{ fontWeight: 600, color: '#1A1B1D', mb: 2 }}>
          Employment Type *
        </FormLabel>
        <Stack direction="row" spacing={2}>
          <Box
            onClick={() => onUpdate({ isFullTime: !data.isFullTime })}
            sx={{
              flex: 1,
              height: '120px',
              padding: '24px',
              borderRadius: '12px',
              backgroundColor: data.isFullTime ? '#F0F2F5' : '#F8F9FA',
              border: data.isFullTime ? '2px solid #2e2d7e' : '1px solid #E5E7EB',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#2e2d7e',
                backgroundColor: data.isFullTime ? '#E8EAED' : '#F0F2F5',
              },
            }}
          >
            <Typography level="h4" sx={{ fontWeight: 600, mb: 1, color: '#1A1B1D' }}>
              Full-time
            </Typography>
            <Typography level="body-sm" sx={{ color: '#7B7F85', lineHeight: 1.3 }}>
              40+ hours per week
            </Typography>
          </Box>
          
          <Box
            onClick={() => onUpdate({ isPartTime: !data.isPartTime })}
            sx={{
              flex: 1,
              height: '120px',
              padding: '24px',
              borderRadius: '12px',
              backgroundColor: data.isPartTime ? '#F0F2F5' : '#F8F9FA',
              border: data.isPartTime ? '2px solid #2e2d7e' : '1px solid #E5E7EB',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#2e2d7e',
                backgroundColor: data.isPartTime ? '#E8EAED' : '#F0F2F5',
              },
            }}
          >
            <Typography level="h4" sx={{ fontWeight: 600, mb: 1, color: '#1A1B1D' }}>
              Part-time
            </Typography>
            <Typography level="body-sm" sx={{ color: '#7B7F85', lineHeight: 1.3 }}>
              Flexible hours
            </Typography>
          </Box>
        </Stack>
      </FormControl>

      {/* Job Description */}
      <FormControl>
        <FormLabel sx={{ fontWeight: 600, color: '#1A1B1D', mb: 1 }}>
          Job Description *
        </FormLabel>
        <Textarea
          value={data.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="Describe the responsibilities, requirements, and ideal person for this position..."
          minRows={6}
          sx={{
            backgroundColor: '#ECEFF3',
            borderRadius: '12px',
            border: 'none',
            fontSize: '1.125rem',
            color: '#1A1B1D',
            '--Textarea-focusedHighlight': 'transparent !important',
            '&::placeholder': {
              color: '#7B7F85 !important',
              opacity: 1,
            },
            '&:hover': {
              backgroundColor: '#ECEFF3',
            },
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              backgroundColor: '#ECEFF3',
            },
          }}
        />
        <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
          {data.description.length} characters (recommended: 100-500)
        </Typography>
      </FormControl>
    </Stack>
  );
};

export default CreatePositionStep;
